import React from "react";
import { Table, CombinedTable } from "@/types/table";
import { TablePosition, TableShape } from "@/hooks/useFloorPlanPositions";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { cn } from "@/lib/utils";
import { RotateCw, MapPin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface FloorPlanEditorToolbarProps {
  position: TablePosition;
  table: Table | CombinedTable;
  cellSize: number;
  zones: FloorPlanZone[];
  onShapeChange: (tableId: string, shape: TableShape) => void;
  onRotate: (tableId: string) => void;
  onAssignToZone: (tableId: string, zoneId: string | null) => void;
}

const getCurrentShape = (width: number, height: number, savedShape?: TableShape): TableShape => {
  if (savedShape === 'round') return 'round';
  if (width === height) return 'square';
  if (width > height) return 'horizontal';
  return 'vertical';
};

export const FloorPlanEditorToolbar = React.memo(({
  position,
  table,
  cellSize,
  zones,
  onShapeChange,
  onRotate,
  onAssignToZone,
}: FloorPlanEditorToolbarProps) => {
  const currentShape = getCurrentShape(position.width, position.height, position.shape);
  const currentZone = zones.find(z => z.id === position.zoneId);
  const capacity = 'capacity' in table ? table.capacity : table.totalCapacity;

  // Position toolbar above the selected table, centered
  const left = position.x * cellSize + (position.width * cellSize) / 2;
  const top = position.y * cellSize - 8;

  return (
    <div
      className="absolute z-[200] flex items-center gap-1.5 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-1.5 md:p-2"
      style={{
        touchAction: 'manipulation',
        left: `${left}px`,
        top: `${top}px`,
        transform: 'translate(-50%, -100%)',
      }}
      data-no-drag
    >
      {/* Table name */}
      <div className="text-xs font-semibold text-foreground px-1 border-r pr-2 min-w-0 max-w-[120px] truncate" title={table.name}>
        {table.name}
        <span className="text-muted-foreground font-normal ml-1">{capacity}p</span>
      </div>

      {/* Shape buttons */}
      <div className="flex gap-0.5 border-r pr-1.5">
        <button
          onClick={() => onShapeChange(position.tableId, 'square')}
          className={cn(
            "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors",
            currentShape === 'square' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
          title="Carré"
        >
          <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-current" />
        </button>
        <button
          onClick={() => onShapeChange(position.tableId, 'round')}
          className={cn(
            "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors",
            currentShape === 'round' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
          title="Rond"
        >
          <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-current rounded-full" />
        </button>
        <button
          onClick={() => onShapeChange(position.tableId, 'horizontal')}
          className={cn(
            "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors",
            currentShape === 'horizontal' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
          title="Rectangle horizontal"
        >
          <div className="w-3.5 h-2 md:w-4 md:h-2.5 border-2 border-current" />
        </button>
        <button
          onClick={() => onShapeChange(position.tableId, 'vertical')}
          className={cn(
            "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors",
            currentShape === 'vertical' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
          title="Rectangle vertical"
        >
          <div className="w-2 h-3.5 md:w-2.5 md:h-4 border-2 border-current" />
        </button>
      </div>

      {/* Rotation button */}
      <button
        onClick={() => onRotate(position.tableId)}
        className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors bg-muted text-muted-foreground hover:bg-muted/80"
        title={`Rotation (${position.rotation || 0}°)`}
      >
        <RotateCw className="w-3.5 h-3.5 md:w-4 md:h-4" />
      </button>

      {/* Zone assignment */}
      {zones.length > 0 && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-colors border-l ml-0.5 pl-0.5",
                currentZone ? "text-foreground" : "text-muted-foreground hover:bg-muted/80"
              )}
              title="Assigner à une zone"
            >
              <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: currentZone?.color || undefined }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top">
            <DropdownMenuItem
              onClick={() => onAssignToZone(position.tableId, null)}
              className={cn(!currentZone && "bg-accent")}
            >
              <span className="w-2 h-2 rounded-full mr-2 bg-muted-foreground/30" />
              Aucune zone
            </DropdownMenuItem>
            {zones.map(zone => (
              <DropdownMenuItem
                key={zone.id}
                onClick={() => onAssignToZone(position.tableId, zone.id)}
                className={cn(currentZone?.id === zone.id && "bg-accent")}
              >
                <span
                  className="w-2 h-2 rounded-full mr-2 shrink-0"
                  style={{ backgroundColor: zone.color }}
                />
                {zone.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
});

FloorPlanEditorToolbar.displayName = 'FloorPlanEditorToolbar';
